// src/pages/AccountsPage.jsx

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next'; 
import { HiCheckCircle, HiXCircle, HiPencil, HiTrash, HiExclamation, HiExternalLink } from 'react-icons/hi';
import Modal from '../components/shared/Modal';

// ===================================================================
// --- Р›РћРљРђР›Р¬РќРђРЇ API Р›РћР“РРљРђ (Р”Р»СЏ РіР°СЂР°РЅС‚РёРё РїРµСЂРµРґР°С‡Рё С‚РѕРєРµРЅР°) ---
// ===================================================================
const API_BASE = 'https://api.hedger.bar/api';
const BYBIT_REGISTER_URL = 'https://partner.bybit.com/b/61198';
const BYBIT_TRANSFER_GUIDE_URL = 'https://hedger.bar/guide/podklyuchenie-birzhi/bybit/kak-smenit-referera-na-birzhe-bybit-s-kompyutera';

// РљР»Р°СЃСЃ РѕС€РёР±РєРё РґР»СЏ РѕР±СЂР°Р±РѕС‚РєРё
class ApiError extends Error {
    constructor(message, status) {
        super(message);
        this.status = status;
        this.name = "ApiError";
    }
}

// РҐРµР»РїРµСЂ РґР»СЏ РїРѕР»СѓС‡РµРЅРёСЏ С‚РѕРєРµРЅР° РЎР’Р•Р–РРњ РїСЂРё РєР°Р¶РґРѕРј РІС‹Р·РѕРІРµ
const getToken = () => localStorage.getItem('authToken');

const fetchApi = async (endpoint, options = {}) => {
  const url = `${API_BASE}${endpoint}`;
  const token = getToken(); // Р‘РµСЂРµРј С‚РѕРєРµРЅ РїСЂСЏРјРѕ РїРµСЂРµРґ Р·Р°РїСЂРѕСЃРѕРј
  
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) {
      headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, { ...options, headers });
  
  if (response.status === 204) return null;
  
  const data = await response.json();
  
  if (!response.ok) {
      throw new ApiError(data.message || `Request failed: ${response.status}`, response.status);
  }
  return data;
};

// Р¤СѓРЅРєС†РёРё API
const getExchangeAccounts = () => fetchApi('/exchange-accounts');
const addExchangeAccount = (data) => fetchApi('/exchange-accounts', { method: 'POST', body: JSON.stringify(data) });
const updateExchangeAccount = (id, data) => fetchApi(`/exchange-accounts/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
const deleteExchangeAccount = (id) => fetchApi(`/exchange-accounts/${id}`, { method: 'DELETE' });

// ===================================================================
// --- РљРћРњРџРћРќР•РќРўР« UI ---
// ===================================================================

const ExchangeIcon = ({ exchange, className = 'h-6 w-6' }) => {
  const id = String(exchange || '').toLowerCase();

  if (id === 'bybit') {
    return <img src="/bybit-logo.png" alt="Bybit" className={`${className} object-contain`} loading="lazy" />;
  }

  if (id === 'bingx') {
    return <img src="/bingx_logo.png" alt="BingX" className={`${className} object-contain`} loading="lazy" />;
  }

  if (id === 'binance') {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path fill="#F0B90B" d="M12 2 14.9 4.9 12 7.8 9.1 4.9 12 2Zm5.1 5.1L20 10l-2.9 2.9L14.2 10l2.9-2.9ZM12 12.2l2.9 2.9L12 18l-2.9-2.9 2.9-2.9Zm-5.1-5.1L9.8 10l-2.9 2.9L4 10l2.9-2.9Zm5.1 2.2L14.9 12 12 14.9 9.1 12 12 9.3Z" />
      </svg>
    );
  }

  if (id === 'mexc') {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path fill="#2ED3B7" d="M4 16.5V7.5L8.5 12 4 16.5Zm16-9v9L15.5 12 20 7.5Z" />
        <path fill="#1A6BFF" d="M8.5 12 12 8.5 15.5 12 12 15.5 8.5 12Z" />
      </svg>
    );
  }

  return <div className={`${className} rounded-full bg-slate-700`} />;
};

const InputField = ({ label, name, type = 'text', value, onChange, placeholder, required }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-slate-300 mb-2">{label}</label>
    <input
      id={name} name={name} type={type} value={value} onChange={onChange}
      placeholder={placeholder} required={required}
      className="w-full rounded-xl border border-slate-700 bg-slate-950/45 px-4 py-3.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-400/60 focus:ring-2 focus:ring-blue-500/20"
    />
  </div>
);

const StatusMessage = ({ type, message }) => {
  if (!message) return null;
  const isSuccess = type === 'success';
  return (
    <div className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm ${isSuccess ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' : 'border-rose-500/30 bg-rose-500/10 text-rose-200'}`}>
      {isSuccess ? <HiCheckCircle className="w-5 h-5" /> : <HiXCircle className="w-5 h-5" />}
      {message}
    </div>
  );
};

const BybitSetupNotice = () => {
  const { t } = useTranslation();

  return (
    <div className="mb-6 overflow-hidden rounded-2xl border border-blue-500/30 bg-gradient-to-br from-slate-900/95 via-slate-800/85 to-blue-950/45 p-5 shadow-[0_20px_40px_-28px_rgba(37,99,235,0.7)]">
      <h4 className="text-base font-semibold text-white">{t('accounts.bybit_help.title')}</h4>
      <p className="mt-2 text-sm text-slate-300 leading-relaxed">{t('accounts.bybit_help.desc')}</p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <a
          href={BYBIT_REGISTER_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-500"
        >
          {t('accounts.bybit_help.btn_register')}
          <HiExternalLink className="h-4 w-4" />
        </a>
        <a
          href={BYBIT_TRANSFER_GUIDE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-600 bg-slate-700/60 px-4 py-3 text-sm font-semibold text-slate-100 transition-colors hover:bg-slate-600/70"
        >
          {t('accounts.bybit_help.btn_transfer')}
          <HiExternalLink className="h-4 w-4" />
        </a>
      </div>
      <p className="mt-3 text-xs text-slate-400">{t('accounts.bybit_help.hint')}</p>
    </div>
  );
};

// РљРѕРјРїРѕРЅРµРЅС‚ РјРѕРґР°Р»СЊРЅРѕРіРѕ РѕРєРЅР° РѕС€РёР±РєРё СЂРµС„РµСЂР°Р»Р°
const ReferralErrorModal = ({ isOpen, onClose, uid }) => {
    const { t } = useTranslation();
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={t('accounts.referral_modal.title')}>
            <div className="text-center p-2">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-rose-500/20 mb-6">
                    <HiExclamation className="h-10 w-10 text-rose-500 animate-pulse" />
                </div>
                
                <h3 className="text-lg font-bold text-white mb-4 leading-tight">
                    {t('accounts.referral_modal.header', { uid: uid || 'вЂ”' })}
                </h3>
                
                <p className="text-slate-300 text-sm leading-relaxed mb-8">
                    {t('accounts.referral_modal.desc')}
                </p>

                <div className="grid grid-cols-1 gap-3">
                    <a 
                        href={BYBIT_REGISTER_URL} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-3.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold transition-all shadow-lg active:scale-95"
                    >
                        {t('accounts.referral_modal.btn_register')}
                        <HiExternalLink className="w-5 h-5" />
                    </a>
                    
                    <a 
                        href={BYBIT_TRANSFER_GUIDE_URL} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-3.5 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-bold transition-all active:scale-95"
                    >
                        {t('accounts.referral_modal.btn_transfer')}
                        <HiExternalLink className="w-5 h-5" />
                    </a>
                </div>

                <p className="mt-6 text-[11px] text-slate-500 uppercase tracking-wider font-medium">
                    {t('accounts.referral_modal.footer')}
                </p>
            </div>
        </Modal>
    );
};

const AccountCard = ({ account, onUpdate, onDelete }) => {
  const { t } = useTranslation();
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-slate-700/70 bg-slate-900/50 p-4 transition-all hover:border-blue-500/40 hover:bg-slate-900/70">
      <div className="pointer-events-none absolute -right-12 -top-14 h-28 w-28 rounded-full bg-blue-500/10 blur-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="truncate font-bold text-white">{account.accountName}</p>
          <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-slate-600/70 bg-slate-800/80 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-slate-300">
            <span className="inline-flex h-4 w-4 items-center justify-center overflow-hidden rounded-full ring-1 ring-slate-500/70 bg-slate-900/80">
              <ExchangeIcon exchange={account.exchange} className="h-4 w-4" />
            </span>
            {account.exchange}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-300">
            <HiCheckCircle className="w-4 h-4" />
            <span>{t('accounts.verified')}</span>
          </div>
          <button onClick={() => onUpdate(account)} title={t('accounts.btns.update_keys')} className="p-2 text-slate-400 rounded-lg border border-slate-700 bg-slate-900/70 hover:border-blue-500/40 hover:text-white transition-colors"><HiPencil className="w-5 h-5" /></button>
          <button onClick={() => onDelete(account)} title={t('accounts.btns.delete_account')} className="p-2 text-slate-400 rounded-lg border border-slate-700 bg-slate-900/70 hover:border-rose-500/40 hover:text-rose-200 transition-colors"><HiTrash className="w-5 h-5" /></button>
        </div>
      </div>
    </div>
  );
};

// --- РњРѕРґР°Р»РєРё Update/Delete ---
const UpdateAccountModal = ({ account, onClose, onSave, status }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ accountName: '', apiKey: '', apiSecret: '' });
  useEffect(() => { if (account) setFormData({ accountName: account.accountName, apiKey: '', apiSecret: '' }); }, [account]);
  const handleSave = () => {
    const updateData = {};
    if (formData.accountName && formData.accountName !== account.accountName) updateData.accountName = formData.accountName;
    if (formData.apiKey) updateData.apiKey = formData.apiKey;
    if (formData.apiSecret) updateData.apiSecret = formData.apiSecret;
    onSave(account, updateData);
  };
  return (
    <Modal isOpen={!!account} onClose={onClose} title={t('accounts.modal.update_title', { name: account?.accountName })}>
      <div className="space-y-4">
        <InputField label={t('accounts.fields.name')} name="accountName" value={formData.accountName} onChange={(e) => setFormData({...formData, accountName: e.target.value})} required />
        <InputField label={t('accounts.fields.api_key_new')} name="apiKey" value={formData.apiKey} onChange={(e) => setFormData({...formData, apiKey: e.target.value})} />
        <InputField label={t('accounts.fields.api_secret_new')} name="apiSecret" type="password" value={formData.apiSecret} onChange={(e) => setFormData({...formData, apiSecret: e.target.value})} />
        <button onClick={handleSave} disabled={status.loading} className="w-full py-3 mt-2 font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700">{status.loading ? t('accounts.btns.saving') : t('accounts.btns.save_changes')}</button>
      </div>
    </Modal>
  );
};

const DeleteConfirmModal = ({ account, onClose, onConfirm, status }) => {
  const { t } = useTranslation();
  return (
    <Modal isOpen={!!account} onClose={onClose} title={t('accounts.modal.delete_title')}>
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-900/50"><HiExclamation className="h-6 w-6 text-red-400" /></div>
        <p className="text-gray-300 mt-4">{t('accounts.modal.delete_confirm', { name: account?.accountName })}</p>
        <div className="mt-6 flex justify-center gap-4">
          <button onClick={onClose} disabled={status.loading} className="px-6 py-2 rounded-lg bg-slate-700 text-white">{t('accounts.btns.cancel')}</button>
          <button onClick={() => onConfirm(account)} disabled={status.loading} className="px-6 py-2 rounded-lg bg-red-600 text-white font-semibold">{status.loading ? t('accounts.btns.deleting') : t('accounts.btns.delete')}</button>
        </div>
      </div>
    </Modal>
  );
};

// --- РћРЎРќРћР’РќРђРЇ РЎРўР РђРќРР¦Рђ ---

const AccountsPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [modalState, setModalState] = useState({ type: null, account: null });
  const [referralModal, setReferralModal] = useState({ isOpen: false, uid: '' });
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [formData, setFormData] = useState({ accountName: '', apiKey: '', apiSecret: '' });
  const [status, setStatus] = useState({ loading: false, error: null, success: null });
  
  const fetchAccounts = async () => {
    if (!user?.login) return;
    setIsLoadingAccounts(true);
    try {
      const response = await getExchangeAccounts();
      setAccounts(response.accounts || []);
    } catch (err) { setAccounts([]); } 
    finally { setIsLoadingAccounts(false); }
  };

  useEffect(() => { fetchAccounts(); }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, error: null, success: null });
    try {
      const response = await addExchangeAccount({ ...formData, exchange: selectedExchange });
      setStatus({ loading: false, success: response.message, error: null });
      setFormData({ accountName: '', apiKey: '', apiSecret: '' });
      await fetchAccounts();
    } catch (err) {
      // Р—РґРµСЃСЊ РёСЃРїРѕР»СЊР·СѓРµРј Р»РѕРєР°Р»СЊРЅС‹Р№ ApiError РёР»Рё СЃС‚Р°РЅРґР°СЂС‚РЅС‹Р№ Error
      const msg = err.message || t('accounts.errors.network');
      
      // Р•РЎР›Р РћРЁРР‘РљРђ Р Р•Р¤Р•Р РђР›Рђ
      if (typeof msg === 'string' && (msg.includes('UID') || msg.includes('СЂРµС„РµСЂР°Р»') || msg.includes('21596908'))) {
         const uidMatch = msg.match(/\d+/);
         setReferralModal({ isOpen: true, uid: uidMatch ? uidMatch[0] : '' });
         // РЎР‘Р РђРЎР«Р’РђР•Рњ СЃС‚Р°С‚СѓСЃ РѕС€РёР±РєРё, С‡С‚РѕР±С‹ РєСЂР°СЃРЅС‹Р№ Р±Р°РЅРЅРµСЂ РќР• РїРѕСЏРІРёР»СЃСЏ
         setStatus({ loading: false, error: null, success: null });
      } else {
         // Р”Р»СЏ РѕР±С‹С‡РЅС‹С… РѕС€РёР±РѕРє (РЅРµРІРµСЂРЅС‹Р№ РєР»СЋС‡ Рё С‚.Рґ.) РїРѕРєР°Р·С‹РІР°РµРј Р±Р°РЅРЅРµСЂ РєР°Рє РѕР±С‹С‡РЅРѕ
         setStatus({ loading: false, error: msg, success: null });
      }
    }
  };

  const handleUpdate = async (account, updateData) => {
    setStatus({ loading: true, error: null, success: null });
    try {
      await updateExchangeAccount(account.publicAccountId, updateData);
      setModalState({ type: null, account: null });
      await fetchAccounts();
      setStatus({ loading: false, success: 'Updated!', error: null });
    } catch (err) { setStatus({ loading: false, error: err.message, success: null }); }
  };

  const handleDelete = async (account) => {
    setStatus({ loading: true, error: null, success: null });
    try {
      await deleteExchangeAccount(account.publicAccountId);
      setModalState({ type: null, account: null });
      await fetchAccounts();
      setStatus({ loading: false, success: 'Deleted!', error: null });
    } catch (err) { setStatus({ loading: false, error: err.message, success: null }); }
  };

  const exchangeList = [
    { id: 'bybit', name: 'Bybit', available: true },
    { id: 'binance', name: 'Binance', available: false },
    { id: 'mexc', name: 'Mexc', available: false },
    { id: 'bingx', name: 'BingX', available: false },
  ];

  const selectedExchangeMeta = exchangeList.find((item) => item.id === selectedExchange);
  const selectedExchangeName = selectedExchangeMeta?.name || '-';

  return (
    <div className="space-y-6 pb-10">
      <section className="relative overflow-hidden rounded-3xl border border-slate-700/60 bg-slate-900/70 px-5 py-6 shadow-2xl sm:px-6 sm:py-7">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(37,99,235,0.22),_transparent_58%)]" />
        <div className="pointer-events-none absolute -left-16 -bottom-20 h-56 w-56 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="relative">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">{t('accounts.page_title')}</h1>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-700/60 bg-slate-800/70 p-5 shadow-xl sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">{t('accounts.your_accounts')}</h2>
          {!isLoadingAccounts && (
            <span className="rounded-full border border-slate-700 bg-slate-900/70 px-3 py-1 text-xs font-semibold text-slate-300">
              {accounts.length}
            </span>
          )}
        </div>
        {isLoadingAccounts ? (
          <div className="space-y-3">
            <div className="h-20 animate-pulse rounded-2xl bg-slate-700/40" />
            <div className="h-20 animate-pulse rounded-2xl bg-slate-700/30" />
          </div>
        ) : accounts.length > 0 ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {accounts.map((acc) => (
              <AccountCard
                key={acc.publicAccountId}
                account={acc}
                onUpdate={() => setModalState({ type: 'update', account: acc })}
                onDelete={() => setModalState({ type: 'delete', account: acc })}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-600/70 bg-slate-900/40 px-4 py-5 text-sm text-slate-400">
            {t('accounts.no_accounts')}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-slate-700/60 bg-slate-800/70 p-5 shadow-xl sm:p-6">
        <h2 className="text-xl font-semibold text-white">{t('accounts.add_title')}</h2>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {exchangeList.map((ex) => (
            <button
              key={ex.id}
              onClick={() => {
                setSelectedExchange(ex.id);
                setStatus({ loading: false, error: null, success: null });
              }}
              disabled={!ex.available}
              className={`group relative overflow-hidden rounded-2xl border px-3 py-4 text-left transition-all ${
                selectedExchange === ex.id
                  ? 'border-blue-500/70 bg-blue-500/10 shadow-[0_16px_28px_-20px_rgba(37,99,235,0.75)]'
                  : 'border-slate-700/70 bg-slate-900/55'
              } ${ex.available ? 'hover:border-blue-400/60 hover:bg-slate-900/80' : 'opacity-55 cursor-not-allowed'}`}
            >
              <div className="flex items-start justify-between gap-2">
                {ex.available ? (
                  <span className="flex items-center gap-2.5 text-lg sm:text-xl font-bold text-white">
                    <span className="inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center overflow-hidden rounded-full ring-1 ring-slate-500/70 bg-slate-900/80">
                      <ExchangeIcon exchange={ex.id} className="h-8 w-8 sm:h-9 sm:w-9" />
                    </span>
                    {ex.name}
                  </span>
                ) : (
                  <span className="text-lg sm:text-xl font-bold text-slate-400">{ex.name}</span>
                )}
                {!ex.available && (
                  <span className="mt-1 shrink-0 text-[10px] font-semibold text-amber-400">
                    {t('accounts.coming_soon')}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>

        {selectedExchange ? (
          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            {selectedExchange === 'bybit' && <BybitSetupNotice />}

            <div className="rounded-2xl border border-slate-700/60 bg-slate-900/40 p-4 sm:p-5">
              <h3 className="text-lg font-semibold text-white tracking-tight">
                {t('accounts.details_for', { exchange: selectedExchangeName })}
              </h3>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label={t('accounts.fields.name')}
                  name="accountName"
                  value={formData.accountName}
                  onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                  placeholder={t('accounts.fields.name_placeholder')}
                  required
                />
                <InputField
                  label={t('accounts.fields.api_key')}
                  name="apiKey"
                  value={formData.apiKey}
                  onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                  required
                />
                <div className="md:col-span-2">
                  <InputField
                    label={t('accounts.fields.api_secret')}
                    name="apiSecret"
                    type="password"
                    value={formData.apiSecret}
                    onChange={(e) => setFormData({ ...formData, apiSecret: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={status.loading}
              className="w-full flex items-center justify-center rounded-xl bg-blue-600 px-4 py-3.5 font-bold text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {status.loading ? t('accounts.btns.connecting') : t('accounts.btns.connect')}
            </button>
            <StatusMessage type="success" message={status.success} />
            <StatusMessage type="error" message={status.error} />
          </form>
        ) : (
          <div className="mt-6 rounded-xl border border-dashed border-slate-600/70 bg-slate-900/40 px-4 py-5 text-sm text-slate-400">
            {t('accounts.select_exchange')}
          </div>
        )}
      </section>

      <UpdateAccountModal account={modalState.type === 'update' ? modalState.account : null} onClose={() => setModalState({ type: null, account: null })} onSave={handleUpdate} status={status} />
      <DeleteConfirmModal account={modalState.type === 'delete' ? modalState.account : null} onClose={() => setModalState({ type: null, account: null })} onConfirm={handleDelete} status={status} />
      
      <ReferralErrorModal 
        isOpen={referralModal.isOpen} 
        onClose={() => setReferralModal({ ...referralModal, isOpen: false })} 
        uid={referralModal.uid} 
      />
    </div>
  );
};

export default AccountsPage;

